import type { PianoChart } from '@shared/pianoChart';
import { PIANO_ENGINE_TUNING, PIANO_HIT_LINE_Y_FRAC } from '@shared/pianoChart';
import { MANIA_LANE_COUNT, type ManiaLane } from '@shared/maniaNote';
import type {
  ManiaReplayInput,
  ManiaReplayInputs,
  ReplayOutcome,
} from '@shared/replay';
import {
  buildManiaEngineConfig,
  step,
  summariseManiaAccuracy,
  type ManiaEngineEvent,
  type ManiaEngineInput,
  type ManiaEngineTuning,
  type ManiaHpChangedPayload,
} from '@shared/engine/mania/core';
import {
  createManiaInitialState,
  resetManiaEngineStateForNextLoop,
  type ManiaEngineConfig,
  type ManiaEngineState,
  type ManiaLaneEvent,
  type ManiaResolution,
} from '@shared/engine/mania/state';
import {
  milestoneIsStar,
  milestoneTier,
  type MilestoneKind,
} from '@shared/progression';
import { RunTimeline } from '../input/runTimeline';
import type { RunTimingReport } from '@shared/antiCheat/runTiming';
import { AudioEngine, clampPlaybackRate } from '../audio/scheduler';
import { loadVolumePrefs } from '../audio/volumePrefs';
import { LoopRunState } from '../loopManager';
import {
  KeyboardPianoInputSource,
  ReplayPianoInputSource,
  type PianoInputSource,
} from './pianoInputSource';
import { PianoRenderer } from './pianoRenderer';
import { DEFAULT_KEY_LABELS } from './render/pianoRenderConstants';
import { describeKeyCode, loadKeybindPrefs } from '../keybindPrefs';
import type {
  PianoFloatingText,
  PianoLaneFlashSpec,
  PianoParticleEmission,
  PianoScorePop,
  PianoShakeSpec,
  PianoShockwaveSpec,
} from './render/pianoRenderTypes';

export type PianoSessionStatus =
  | 'idle'
  | 'starting'
  | 'lead_in'
  | 'running'
  | 'paused'
  | 'awaiting_next_loop'
  | 'completed'
  | 'failed';

export interface PianoMilestoneInfo {
  kind: MilestoneKind;
  loopIndex: number;
  starsEarned: number;
  cupsEarned: number;
  pause: boolean;
}

export interface PianoGameSessionOptions {
  chart: PianoChart;
  canvas: HTMLCanvasElement;
  audioOffsetMs: number;
  /** Pre-recorded inputs (for replay verifiers / debug). */
  replayInputs?: ManiaEngineInput[];
  onStatusChange?: (status: PianoSessionStatus) => void;
  onComplete?: (outcome: ReplayOutcome) => void;
  onEvent?: (event: ManiaEngineEvent) => void;
  onMilestone?: (info: PianoMilestoneInfo) => void;
  testHook?: boolean;
  enableLoops?: boolean;
  /**
   * Override Piano's default instadeath-on-miss behaviour. Real play
   * leaves this undefined and inherits {@link PIANO_ENGINE_TUNING}
   * (instadeath on); the tutorial passes `false` so a beginner can
   * miss a tile and keep practising the rest of the lesson chart.
   */
  instadeathOnMiss?: boolean;
}

const LEAD_IN_BEATS = 4;
/** See `MIN_LEAD_IN_WALL_SECONDS` in circlesSession.ts — same logic. */
const MIN_LEAD_IN_WALL_SECONDS = 2.0;
/** Score-roll-up easing. See SCORE_DISPLAY_LERP_RATE in circlesSession.ts. */
const SCORE_DISPLAY_LERP_RATE = 15;
/** Floating-text lifetime in seconds for verdict pops. */
const FLOATING_TEXT_LIFE = 0.55;
/** Bigger / longer lifetime for centre-screen combo milestone banners. */
const BIG_FLOATING_TEXT_LIFE = 1.1;
/** Score-pop flyer lifetime. */
const SCORE_POP_LIFE = 0.75;
/** Combos at which to fire a celebration banner + fanfare. Matches Circles' COMBO_MILESTONES. */
const COMBO_MILESTONES = [10, 25, 50, 100] as const;
/** Combo at which a single miss becomes a "big combo break" — bigger shake + deeper duck. */
const BIG_COMBO_BREAK_THRESHOLD = 25;
/**
 * Cadence (seconds) at which a sustained hold emits an ambient
 * "you're scoring" sparkle. ~9 per second per held lane: dense enough
 * to feel continuous, sparse enough that long holds don't snow the
 * playfield. Reset to zero phase when a lane leaves heldLanes.
 */
const HELD_SPARKLE_INTERVAL_SEC = 0.11;
/**
 * Hit-stop micro-freeze tunings (ms). Asymmetric across verdicts so
 * the freeze duration itself telegraphs how well the player did:
 * perfects get a meaty hold, big-combo perfects almost touch the
 * renderer's HIT_STOP_MAX_MS=80 cap, goods get a tiny snap, misses
 * fire no freeze at all (the screen shake + duck carry the feedback).
 */
const HIT_STOP_MS_PERFECT = 50;
const HIT_STOP_MS_PERFECT_BIG_COMBO = 70;
const HIT_STOP_MS_GOOD = 20;
/** Music duck-pulse tunings on miss and on big-combo break. Matches Circles. */
const MUSIC_DUCK_MISS = { dip: 0.72, attackMs: 25, releaseMs: 90 };
const MUSIC_DUCK_COMBO_BREAK = { dip: 0.5, attackMs: 40, releaseMs: 280 };
/** Verdict colours, fixed across charts so muscle-memory doesn't get palette-shuffled. */
const VERDICT_COLOURS = {
  perfect: '#3bff9d',
  good: '#16e0ff',
  miss: '#ff3b5c',
} as const;
const MILESTONE_COLOUR = '#ffd84d';
const LIFELINE_COLOUR = '#3bd8ff';
const DEATH_COLOUR = '#ff3b5c';

function effectiveLeadInBeats(baseBeats: number, bpm: number, startSpeed: number): number {
  const effectiveSpeed = clampPlaybackRate(startSpeed);
  const wallTime = (baseBeats * 60) / bpm / effectiveSpeed;
  if (wallTime >= MIN_LEAD_IN_WALL_SECONDS) {
    return baseBeats;
  }
  return Math.ceil((MIN_LEAD_IN_WALL_SECONDS * bpm * effectiveSpeed) / 60);
}

/**
 * Read the current per-lane primary key labels from keybind prefs,
 * falling back to the defaults (Q / W / E / R) when a slot is empty.
 * Used by the renderer to paint the labels under the hit line so the
 * displayed letter always matches what the player has bound. Mirrors
 * {@link import('../guitar/guitarSession').loadGuitarKeyLabels} so
 * both modes follow the same pattern.
 */
function loadPianoKeyLabels(): Record<ManiaLane, string> {
  const prefs = loadKeybindPrefs();
  const out = { ...DEFAULT_KEY_LABELS } as Record<ManiaLane, string>;
  for (let lane = 0; lane < MANIA_LANE_COUNT; lane += 1) {
    const lanePrefs = prefs.pianoLanes[lane];
    const primary = lanePrefs[0];
    if (primary) {
      out[lane as ManiaLane] = describeKeyCode(primary);
    }
  }
  return out;
}

/**
 * Piano-mode game session. Mirrors {@link
 * import('../circles/circlesSession.js').CirclesGameSession}'s
 * lifecycle so PlayPage code parallels cleanly between modes:
 *
 *   constructor → start() → tick loop → pause/resume → destroy
 *
 * Reuses the same {@link AudioEngine}, {@link LoopRunState}, and the
 * universal star/cup milestone vocabulary. Mode-specific bits are the
 * engine, renderer, and keyboard / pointer-per-lane input source.
 *
 * Visual feedback parallels the Circles juice pipeline: every
 * resolution fires a particle burst, a lane-column flash, an
 * expanding shockwave, a "+N" score-pop arcing toward the HUD, a
 * short hit-stop on perfects, and a screen shake whose amount
 * scales with verdict + combo break. Combo milestones (10x / 25x /
 * 50x / 100x) trigger centre-screen banners with fanfare SFX, and
 * misses pulse-duck the music so the SFX punch reads through.
 */
export class PianoGameSession {
  private chart: PianoChart;
  private engineTuning: ManiaEngineTuning;
  /** Resolved instadeath flag — drives the engine config and the HUD (HP bar hidden when on). */
  private instadeathOnMiss: boolean;
  private engineConfig: ManiaEngineConfig;
  private state: ManiaEngineState;
  private audio: AudioEngine;
  private renderer: PianoRenderer;
  private inputSource: PianoInputSource;
  private floatingTexts: PianoFloatingText[] = [];
  private scorePops: PianoScorePop[] = [];
  private shake: PianoShakeSpec = { amount: 0, decay: 0 };
  /**
   * Per-frame FX-spec buffers. Cleared on every tick after the
   * renderer consumes them so the same emission isn't double-spawned
   * across frames (the renderer also de-dupes by identity for the
   * paranoia case where a frame fires twice via rAF reentry).
   */
  private particleEmissionsThisFrame: PianoParticleEmission[] = [];
  private shockwaveSpecsThisFrame: PianoShockwaveSpec[] = [];
  private laneFlashSpecsThisFrame: PianoLaneFlashSpec[] = [];
  private rafId: number | null = null;
  private lastBeat = 0;
  private status: PianoSessionStatus = 'idle';
  private opts: PianoGameSessionOptions;
  private outcome: ReplayOutcome | null = null;
  private allEvents: ManiaEngineEvent[] = [];
  /** Per-loop captured inputs. See `loopCapturedInputs` in circlesSession.ts. */
  private loopCapturedInputs: ManiaReplayInput[][] = [[]];
  /** Anti-cheat timing recorder (real-time progression + isTrusted). */
  private timeline: RunTimeline;
  private loopState: LoopRunState;
  private enableLoops: boolean;
  private destroyed = false;
  private autoPaused = false;
  private lastTickAt = performance.now();
  /** See `displayedScore` in circlesSession.ts. */
  private displayedScore = 0;
  /** Combo milestones already celebrated this loop — reset on combo break + on loop wrap. */
  private comboMilestonesHit = new Set<number>();
  /** Wall-clock timestamp of the most recent perfect-shake. Rate-limits perfect-shake on dense streams. */
  private lastPerfectShakeAt = 0;
  /**
   * Per-lane accumulator (seconds) driving the ambient "you're
   * scoring" sparkle stream during active holds. Cleared when the
   * lane leaves the engine's heldLanes set so the next hold starts
   * fresh instead of inheriting leftover phase.
   */
  private heldSparkleTimers: Map<ManiaLane, number> = new Map();
  private reducedMotion = false;
  /**
   * Canvas size in CSS px, kept in sync via a `ResizeObserver`. The
   * size only changes on viewport resize / orientation change, so
   * caching it lets the tick loop and FX-spawn helpers read width /
   * height synchronously without triggering a layout flush every
   * frame (which `getBoundingClientRect()` can do on mobile browsers).
   */
  private cachedRect: { width: number; height: number } = { width: 0, height: 0 };
  private resizeObserver: ResizeObserver | null = null;
  private keyLabels: Record<ManiaLane, string>;

  constructor(opts: PianoGameSessionOptions) {
    this.opts = opts;
    this.chart = opts.chart;
    this.enableLoops = opts.enableLoops ?? false;
    // Real play inherits PIANO_ENGINE_TUNING's instadeath; the tutorial
    // passes `instadeathOnMiss: false` to stay forgiving. Resolve once
    // here so the engine config and the HUD agree on whether the HP bar
    // is meaningful this run.
    this.instadeathOnMiss = opts.instadeathOnMiss ?? PIANO_ENGINE_TUNING.instadeathOnMiss ?? false;
    this.engineTuning = { ...PIANO_ENGINE_TUNING, instadeathOnMiss: this.instadeathOnMiss };
    this.loopState = new LoopRunState(this.chart.totalBeats);
    this.engineConfig = buildManiaEngineConfig(this.chart, this.engineTuning);
    this.state = createManiaInitialState(this.chart);
    this.audio = new AudioEngine({ audioOffsetMs: opts.audioOffsetMs });
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
    this.renderer = new PianoRenderer(opts.canvas);
    this.attachResizeObserver();
    this.keyLabels = loadPianoKeyLabels();

    if (opts.replayInputs) {
      this.inputSource = new ReplayPianoInputSource(opts.replayInputs);
    } else {
      this.inputSource = new KeyboardPianoInputSource(
        opts.canvas,
        () => this.audio.getCurrentBeat(),
        () => this.status === 'lead_in' || this.status === 'running',
      );
    }

    this.timeline = new RunTimeline({
      getBeatAtNow: () => this.audio.getCurrentBeat(),
      isClockRunning: () => this.status === 'running',
    });
    this.timeline.attach();

    if (opts.testHook) {
      (window as unknown as {
        __beatHeavenPianoTestHook?: PianoGameSessionTestHook;
      }).__beatHeavenPianoTestHook = this.buildTestHook();
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      if (this.status === 'running' || this.status === 'lead_in') {
        this.autoPaused = true;
        this.pause();
      }
    } else if (this.autoPaused && this.status === 'paused') {
      this.autoPaused = false;
      this.resume().catch((err) => {
        console.warn('Auto-resume after tab return failed:', err);
      });
    }
  };

  async start(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.start(this.chart, leadIn, { speedRamp: ramp });
    if (this.destroyed) {
      return;
    }
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.tick();
  }

  async startNextLoop(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    if (this.status !== 'awaiting_next_loop') {
      return;
    }
    this.loopState.advanceLoop();
    resetManiaEngineStateForNextLoop(this.state);
    this.engineConfig = buildManiaEngineConfig(this.chart, this.engineTuning);
    this.lastBeat = 0;
    this.loopCapturedInputs.push([]);
    this.timeline.startLoop(this.loopCapturedInputs.length - 1);
    this.comboMilestonesHit.clear();
    // Clear session-owned FX too, not just renderer state — a banner
    // / shake / score-pop in the final beats of loop N would
    // otherwise ghost-render through loop N+1's opening countdown.
    this.floatingTexts.length = 0;
    this.scorePops.length = 0;
    this.shake.amount = 0;
    this.shake.decay = 0;
    this.lastPerfectShakeAt = 0;
    this.heldSparkleTimers.clear();
    this.renderer.clearLoopBoundFx();

    this.setStatus('starting');
    const ramp = this.loopState.getCurrentSpeedRamp();
    const leadIn = effectiveLeadInBeats(LEAD_IN_BEATS, this.chart.bpm, ramp.startSpeed);
    await this.audio.restartForLoop(this.chart, leadIn, ramp);
    if (this.destroyed) {
      return;
    }
    this.audio.scheduleLeadInTicks(leadIn);
    this.setStatus('lead_in');
    if (document.hidden) {
      this.autoPaused = true;
      this.pause();
      return;
    }
    this.lastTickAt = performance.now();
    this.tick();
  }

  getCurrentBeat(): number {
    return this.audio.getCurrentBeat();
  }

  pause(): void {
    if (this.status === 'running' || this.status === 'lead_in') {
      this.setStatus('paused');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      void this.audio.suspend().catch((err) => {
        console.warn('Failed to suspend audio on pause:', err);
      });
    }
  }

  manualPause(): void {
    this.autoPaused = false;
    this.pause();
  }

  async resume(): Promise<void> {
    if (this.status === 'paused') {
      this.autoPaused = false;
      await this.audio.resume();
      this.setStatus('running');
      this.tick();
    }
  }

  async destroy(): Promise<void> {
    this.destroyed = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.inputSource.destroy();
    this.timeline.detach();
    await this.audio.stop();
    delete (window as unknown as {
      __beatHeavenPianoTestHook?: PianoGameSessionTestHook;
    }).__beatHeavenPianoTestHook;
  }

  getOutcome(): ReplayOutcome | null {
    return this.outcome;
  }

  getStatus(): PianoSessionStatus {
    return this.status;
  }

  getCapturedReplay(): ManiaReplayInputs {
    return {
      loops: this.loopCapturedInputs.map((inputs) => ({ inputs: inputs.slice() })),
      audioOffsetMs: this.opts.audioOffsetMs,
    };
  }

  /** Anti-cheat timing stream for this run (null if nothing captured). */
  getTimingReport(): RunTimingReport | null {
    return this.timeline.toReport();
  }

  getProgressionEarnedThisRun(): { stars: number; cups: number } {
    return {
      stars: this.loopState.countStars(),
      cups: this.loopState.countCups(),
    };
  }

  getLoopIndex(): number {
    return this.loopState.getLoopIndex();
  }

  getLoopsCleared(): number {
    return this.loopState.getLoopIndex();
  }

  applyVolumePrefs(): void {
    const vols = loadVolumePrefs();
    this.audio.setMasterVolume(vols.master);
    this.audio.setMusicVolume(vols.music);
    this.audio.setSfxVolume(vols.sfx);
  }

  private setStatus(s: PianoSessionStatus): void {
    if (this.status === s) {
      return;
    }
    this.status = s;
    this.opts.onStatusChange?.(s);
  }

  private tick = (): void => {
    if (this.status === 'paused') {
      return;
    }

    const currentBeat = this.audio.getCurrentBeat();
    const now = performance.now();
    const dt = Math.min(0.05, Math.max(0, (now - this.lastTickAt) / 1000));
    this.lastTickAt = now;

    if (currentBeat < 0) {
      const leadInBeat = this.computeLeadInBeat();
      this.drawFrame(currentBeat, leadInBeat, dt);
      this.rafId = requestAnimationFrame(this.tick);
      return;
    }

    if (this.status === 'lead_in') {
      this.setStatus('running');
    }

    const cappedTarget = Math.min(currentBeat, this.lastBeat + this.audio.maxAdvanceBeatsPerFrame());

    // Clamp each input into `[this.lastBeat, cappedTarget]` (also capped
    // at `totalBeats`) rather than dropping out-of-range taps:
    //   - Lead-in taps arrive stamped with a NEGATIVE beat (the audio
    //     clock is still pre-roll). Clamping them up to `this.lastBeat`
    //     (beat 0 on the opening frame) lets the opening tiles be hit by a
    //     tap thrown in the final moments of the countdown instead of
    //     silently eating the press. (The engine exempts these beat-0
    //     taps from the empty-tap penalty — see `handleTap`.)
    //   - Late taps the player squeezes in after the song ends get pulled
    //     back to `totalBeats`. The headless runner (`runManiaReplay`)
    //     clamps its tick window to `totalBeats`, so unclamped late inputs
    //     would be silently dropped on re-simulation and live + replay
    //     would disagree on the final tile's verdict.
    // Every captured beat therefore stays in `[0, totalBeats]`, so the
    // replay verifier (which steps from beat 0) re-simulates identically.
    const totalBeats = this.chart.totalBeats;
    const rawInputs = this.inputSource.poll(cappedTarget);
    const clampedInputs: ManiaEngineInput[] = rawInputs.map((inp) => ({
      beat: Math.max(
        this.lastBeat,
        Math.min(inp.beat, cappedTarget, totalBeats),
      ),
      lane: inp.lane,
      type: inp.type,
      source: inp.source,
    }));
    const activeLoopCapture = this.loopCapturedInputs[this.loopCapturedInputs.length - 1];
    for (const inp of clampedInputs) {
      activeLoopCapture.push({
        beat: inp.beat,
        lane: inp.lane,
        type: inp.type,
        source: inp.source,
      });
    }

    const out = step({
      config: this.engineConfig,
      state: this.state,
      fromBeat: this.lastBeat,
      toBeat: cappedTarget,
      inputs: clampedInputs,
    });
    this.lastBeat = Math.max(this.lastBeat, cappedTarget);
    for (const ev of out.events) {
      this.handleEvent(ev);
      this.allEvents.push(ev);
      this.opts.onEvent?.(ev);
    }

    this.detectAndFireMidRunMilestones();
    this.tickDisplayedScore(dt);
    this.advanceFloatingTexts(dt);
    this.advanceScorePops(dt);
    this.advanceShake(dt);
    this.maintainHeldSparkles(dt);

    this.drawFrame(this.state.beat, null, dt);

    // Per-frame spec buffers are drained by the renderer; reset
    // before the next tick so we don't re-spawn yesterday's effects.
    this.particleEmissionsThisFrame = [];
    this.shockwaveSpecsThisFrame = [];
    this.laneFlashSpecsThisFrame = [];

    if (!this.state.alive && this.status !== 'failed') {
      this.finishSession('failed');
      return;
    }
    if (this.state.completed) {
      this.handleLoopComplete();
      return;
    }

    this.rafId = requestAnimationFrame(this.tick);
  };

  private detectAndFireMidRunMilestones(): void {
    if (!this.enableLoops) {
      return;
    }
    const candidates = this.loopState.detectMidRunMilestones(this.state.beat);
    for (const kind of candidates) {
      if (this.loopState.markFired(kind)) {
        this.audio.playMilestoneFanfare(milestoneTier(kind));
        this.spawnMilestoneFx(kind);
        this.opts.onMilestone?.({
          kind,
          loopIndex: this.loopState.getLoopIndex(),
          starsEarned: this.loopState.countStars(),
          cupsEarned: this.loopState.countCups(),
          pause: false,
        });
      }
    }
  }

  private tickDisplayedScore(dt: number): void {
    const target = this.state.score;
    if (this.displayedScore < target) {
      const k = 1 - Math.exp(-SCORE_DISPLAY_LERP_RATE * dt);
      this.displayedScore += (target - this.displayedScore) * k;
      if (target - this.displayedScore < 0.5) {
        this.displayedScore = target;
      }
    } else if (this.displayedScore > target) {
      this.displayedScore = target;
    }
  }

  private advanceFloatingTexts(dt: number): void {
    if (this.floatingTexts.length === 0) {
      return;
    }
    // Mutate in place so object identity persists across frames — the
    // renderer's `consumedBigBanners` WeakSet keys off it to fire the
    // milestone-banner entry shockwave exactly once per banner.
    const survivors: PianoFloatingText[] = [];
    for (const t of this.floatingTexts) {
      if (!t.big) {
        t.y -= 50 * dt;
      }
      t.life -= dt;
      if (t.life > 0) {
        survivors.push(t);
      }
    }
    this.floatingTexts = survivors;
  }

  private advanceScorePops(dt: number): void {
    if (this.scorePops.length === 0) {
      return;
    }
    // Mirrors advanceFloatingTexts: mutate in place so object identity
    // is preserved for any future renderer-side one-shot effects keyed
    // off the spec reference.
    const survivors: PianoScorePop[] = [];
    for (const p of this.scorePops) {
      p.life -= dt;
      if (p.life > 0) {
        survivors.push(p);
      }
    }
    this.scorePops = survivors;
  }

  private advanceShake(dt: number): void {
    if (this.shake.amount > 0) {
      this.shake.amount = Math.max(0, this.shake.amount - this.shake.decay * dt);
    }
  }

  /**
   * Ambient "you're scoring right now" sparkle stream for actively
   * engaged holds. Each frame, for every lane in the engine's
   * heldLanes set, accumulate dt and emit a small upward sparkle
   * every {@link HELD_SPARKLE_INTERVAL_SEC} — gives continuous
   * positive feedback distinct from the per-resolution radial burst
   * so the player feels rewarded throughout the hold, not just at
   * its endpoints. Timers are cleared when a lane is released so a
   * subsequent hold starts at zero phase instead of inheriting
   * leftover progress.
   */
  private maintainHeldSparkles(dt: number): void {
    if (this.reducedMotion || dt <= 0) {
      return;
    }
    const held = this.state.heldLanes;
    if (held.size === 0) {
      if (this.heldSparkleTimers.size > 0) {
        this.heldSparkleTimers.clear();
      }
      return;
    }
    for (const lane of this.heldSparkleTimers.keys()) {
      if (!held.has(lane)) {
        this.heldSparkleTimers.delete(lane);
      }
    }
    for (const lane of held.keys()) {
      const prev = this.heldSparkleTimers.get(lane) ?? 0;
      let next = prev + dt;
      while (next >= HELD_SPARKLE_INTERVAL_SEC) {
        next -= HELD_SPARKLE_INTERVAL_SEC;
        const { x, y } = this.laneHitPoint(lane);
        this.particleEmissionsThisFrame.push({
          type: 'hold_sustain',
          x,
          y,
          colour: this.chart.palette.padPerfect,
        });
      }
      this.heldSparkleTimers.set(lane, next);
    }
  }

  private handleLoopComplete(): void {
    const completionMilestone = this.enableLoops
      ? this.loopState.detectCompletionMilestone()
      : null;
    if (completionMilestone && this.loopState.markFired(completionMilestone)) {
      this.audio.playMilestoneFanfare(milestoneTier(completionMilestone));
      this.spawnMilestoneFx(completionMilestone);
      const isLoopOneFinale =
        this.loopState.getLoopIndex() === 0 && milestoneIsStar(completionMilestone);
      this.opts.onMilestone?.({
        kind: completionMilestone,
        loopIndex: this.loopState.getLoopIndex(),
        starsEarned: this.loopState.countStars(),
        cupsEarned: this.loopState.countCups(),
        pause: isLoopOneFinale,
      });
    }
    if (this.enableLoops) {
      this.setStatus('awaiting_next_loop');
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      const isLoopOneFinale = this.loopState.getLoopIndex() === 0;
      if (isLoopOneFinale) {
        return;
      }
      void this.startNextLoop();
      return;
    }
    if (this.status !== 'completed') {
      this.finishSession('completed');
    }
  }

  private computeLeadInBeat(): number | null {
    const songTime = this.audio.getSongTime();
    if (!isFinite(songTime)) {
      return null;
    }
    if (songTime >= 0) {
      return null;
    }
    const beatsRemaining = -songTime * (this.chart.bpm / 60);
    return Math.max(0, beatsRemaining);
  }


  /**
   * Pixel coordinates of the visual centre of a lane at the hit
   * line. Delegates to {@link PianoRenderer.getLaneHitPoint} so lane
   * geometry stays single-source — the session shouldn't ever
   * recompute layout constants the renderer already owns.
   */
  private laneHitPoint(lane: number): { x: number; y: number } {
    return this.renderer.getLaneHitPoint(
      lane as 0 | 1 | 2 | 3,
      this.cachedRect.width,
      this.cachedRect.height,
    );
  }

  /**
   * Seed `cachedRect` from the canvas's current bounds and attach a
   * `ResizeObserver` so subsequent updates fire only on actual
   * viewport changes. Replaces the per-frame `getBoundingClientRect`
   * call — `RO` callbacks dispatch from the browser's layout flush,
   * so they're guaranteed-correct without our tick loop forcing
   * layout to read the size.
   */
  private attachResizeObserver(): void {
    const rect = this.opts.canvas.getBoundingClientRect();
    this.cachedRect.width = rect.width;
    this.cachedRect.height = rect.height;
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      this.cachedRect.width = entry.contentRect.width;
      this.cachedRect.height = entry.contentRect.height;
    });
    this.resizeObserver.observe(this.opts.canvas);
  }

  private drawFrame(beat: number, leadInBeat: number | null, _dt: number): void {
    this.renderer.draw({
      chart: this.chart,
      state: this.state,
      laneEvents: this.engineConfig.sortedLaneEvents as ReadonlyArray<ManiaLaneEvent>,
      beat,
      width: this.cachedRect.width,
      height: this.cachedRect.height,
      pressedLanes: this.inputSource.getPressedLanes(),
      displayedScore: this.displayedScore,
      countdown: leadInBeat,
      floatingTexts: this.floatingTexts,
      particleEmissions: this.particleEmissionsThisFrame,
      shockwaveSpecs: this.shockwaveSpecsThisFrame,
      laneFlashSpecs: this.laneFlashSpecsThisFrame,
      scorePops: this.scorePops,
      shake: this.shake,
      paused: this.status === 'paused',
      perfectOverlapBeats: this.engineConfig.perfectOverlapBeats,
      keyLabels: this.keyLabels,
      instadeath: this.instadeathOnMiss,
    });
  }

  private spawnVerdictText(res: ManiaResolution): void {
    // Piano emits perfect / good / miss; the 'ok' branch is impossible
    // today (no `okWindowMs` is configured) but we narrow explicitly
    // so a future engine change doesn't silently produce undefined
    // colours via the keyof cast.
    const pianoVerdict: 'perfect' | 'good' | 'miss' =
      res.verdict === 'perfect' ? 'perfect' : res.verdict === 'miss' ? 'miss' : 'good';
    // Release events get a distinct label so the player can tell at a
    // glance whether the verdict was for the tap (head) or the let-go
    // (release). Same colour code so muscle-memory carries through.
    let label: string;
    if (res.kind === 'release') {
      label = pianoVerdict === 'miss' ? 'BROKEN' : 'RELEASE';
    } else {
      label = pianoVerdict === 'perfect' ? 'PERFECT' : pianoVerdict === 'good' ? 'Good' : 'Miss';
    }
    const colour = VERDICT_COLOURS[pianoVerdict];
    const hitLineY = this.cachedRect.height * PIANO_HIT_LINE_Y_FRAC;
    this.floatingTexts.push({
      text: label,
      lane: res.lane,
      y: hitLineY - 30,
      colour,
      life: FLOATING_TEXT_LIFE,
      maxLife: FLOATING_TEXT_LIFE,
    });
  }

  /**
   * Big centre-screen banner used for combo milestones (x10 / x25 /
   * x50 / x100) and dramatic events (LIFELINE, GAME OVER). Different
   * lifecycle from verdict pops: stays put rather than drifting,
   * scales in then fades over a longer life.
   */
  private spawnBigBanner(text: string, colour: string): void {
    this.floatingTexts.push({
      text,
      lane: 0,
      y: 0,
      colour,
      life: BIG_FLOATING_TEXT_LIFE,
      maxLife: BIG_FLOATING_TEXT_LIFE,
      big: true,
    });
  }

  private spawnScorePop(
    lane: number,
    verdict: 'perfect' | 'good',
    scoreDelta: number,
    newCombo: number,
  ): void {
    const { x, y } = this.laneHitPoint(lane);
    const target = this.renderer.getScoreHudTarget(
      this.cachedRect.width,
      this.cachedRect.height,
    );
    const colour = verdict === 'perfect' ? VERDICT_COLOURS.perfect : VERDICT_COLOURS.good;
    const big = verdict === 'perfect' && newCombo >= BIG_COMBO_BREAK_THRESHOLD;
    this.scorePops.push({
      text: `+${Math.round(scoreDelta)}`,
      startX: x,
      startY: y - 40,
      targetX: target.x,
      targetY: target.y,
      colour,
      big,
      life: SCORE_POP_LIFE,
      maxLife: SCORE_POP_LIFE,
    });
  }

  private spawnHitFx(
    lane: number,
    verdict: 'perfect' | 'good' | 'miss',
    newCombo: number,
    kind: ManiaResolution['kind'],
  ): void {
    const { x, y } = this.laneHitPoint(lane);
    const colour =
      verdict === 'perfect'
        ? VERDICT_COLOURS.perfect
        : verdict === 'good'
          ? VERDICT_COLOURS.good
          : VERDICT_COLOURS.miss;
    const isSuccessfulRelease = kind === 'release' && verdict !== 'miss';

    // Particle burst at the hit line, intensity scales with verdict.
    this.particleEmissionsThisFrame.push({
      type:
        verdict === 'perfect'
          ? 'hit_perfect'
          : verdict === 'good'
            ? 'hit_good'
            : 'hit_miss',
      x,
      y,
      colour,
    });

    // Successful hold release earns an extra upward plume on TOP of
    // the radial verdict burst — the radial says "you hit the
    // timing," the plume says "you held it all the way." Reads as
    // stored energy escaping skyward when the player lets go.
    if (isSuccessfulRelease) {
      this.particleEmissionsThisFrame.push({
        type: 'hold_release',
        x,
        y,
        colour,
      });
    }

    // Lane column flash — asymmetric by verdict so the column itself
    // tells the player the outcome at peripheral vision: perfects
    // glow longer & brighter (you nailed it; bask in it), goods hold
    // a softer mid-life cyan, misses snap-fade in <0.2s (acknowledged
    // and moving on, not lingering). Hold releases stretch the flash
    // a little longer so the lane visibly "exhales" after a held
    // commitment, distinguishing it from a tap-and-go.
    const flashAlpha = verdict === 'perfect' ? 0.55 : verdict === 'good' ? 0.3 : 0.4;
    const baseFlashLife = verdict === 'perfect' ? 0.32 : verdict === 'good' ? 0.18 : 0.18;
    const flashLife = isSuccessfulRelease ? baseFlashLife + 0.12 : baseFlashLife;
    this.laneFlashSpecsThisFrame.push({
      lane: lane as 0 | 1 | 2 | 3,
      life: flashLife,
      alpha: isSuccessfulRelease ? flashAlpha + 0.1 : flashAlpha,
      colour,
    });

    // Shockwave rings on successful hits. Big-combo perfects get a
    // second layered ring for extra punch. Successful hold releases
    // get a larger primary ring (~+30 endR) so the moment reads as
    // weightier than a tap — proportional to the longer commitment.
    if (verdict !== 'miss') {
      const baseR = verdict === 'perfect' ? 28 : 22;
      const tapEndR = verdict === 'perfect' ? 110 : 80;
      const endR = isSuccessfulRelease ? tapEndR + 30 : tapEndR;
      this.shockwaveSpecsThisFrame.push({
        x,
        y,
        startR: baseR,
        endR,
        life: verdict === 'perfect' ? 0.45 : 0.32,
        thickness: 3,
        colour,
        alpha: 0.75,
      });
      if (verdict === 'perfect' && newCombo >= BIG_COMBO_BREAK_THRESHOLD) {
        this.shockwaveSpecsThisFrame.push({
          x,
          y,
          startR: 14,
          endR: 160,
          life: 0.6,
          thickness: 2,
          colour: MILESTONE_COLOUR,
          alpha: 0.5,
        });
      }
    }
  }

  private addShake(amount: number): void {
    if (this.reducedMotion) {
      return;
    }
    this.shake.amount = Math.max(this.shake.amount, amount);
    this.shake.decay = amount * 6;
  }

  /**
   * Rate-limited per-perfect shake. Without the gate, dense charts at
   * ~15 perfects/sec stack shake faster than its decay (15 units/sec)
   * can drain it, producing a constant ±2.5px screen jitter that
   * stops reading as "reactive" and starts reading as "the screen is
   * vibrating." The 120ms floor lets a sustained stream feel rhythmic
   * (one shake per ~7 perfects at 14 hits/sec) while still giving
   * every isolated perfect its punch.
   */
  private maybeAddPerfectShake(): void {
    const now = performance.now();
    if (now - this.lastPerfectShakeAt < 120) {
      return;
    }
    this.lastPerfectShakeAt = now;
    this.addShake(2.5);
  }

  /**
   * Combo milestone celebrations. Called whenever `state.combo`
   * passes one of {@link COMBO_MILESTONES}. Fires the fanfare SFX,
   * spawns a centre-screen banner, and adds a small shake to make
   * the moment land. Tracked per-loop so re-passing a threshold
   * (e.g. break combo at 30 → climb back to 25) only celebrates the
   * first time inside a single combo chain.
   */
  private maybeCelebrateComboMilestone(combo: number): void {
    for (const milestone of COMBO_MILESTONES) {
      // `>=` (not `===`) so a chord that vaults the player from combo
      // 9 to 11 in one step doesn't silently skip the x10 banner.
      // `comboMilestonesHit` already dedupes so re-passing the same
      // threshold can't fire twice within a single combo chain.
      if (combo >= milestone && !this.comboMilestonesHit.has(milestone)) {
        this.comboMilestonesHit.add(milestone);
        const tier = milestone >= 100 ? '#ff2bd6' : milestone >= 50 ? '#ffd84d' : milestone >= 25 ? '#3bff9d' : '#16e0ff';
        this.spawnBigBanner(`x${milestone} COMBO!`, tier);
        this.audio.scheduleComboMilestone();
        this.addShake(milestone >= 50 ? 8 : 5);
      }
    }
  }

  private spawnMilestoneFx(_kind: MilestoneKind): void {
    const centreX = this.cachedRect.width / 2;
    const centreY = this.cachedRect.height * 0.5;
    // Big yellow burst at centre + cross-screen shake — the moment
    // should feel celebratory regardless of where on the field the
    // milestone resolved.
    this.particleEmissionsThisFrame.push({
      type: 'milestone',
      x: centreX,
      y: centreY,
      colour: MILESTONE_COLOUR,
    });
    this.addShake(8);
  }

  private handleEvent(ev: ManiaEngineEvent): void {
    if (ev.type === 'note_judged') {
      const res = ev.payload as ManiaResolution;
      this.spawnVerdictText(res);
      // Per-event combo derived from `previousCombo` rather than
      // `state.combo`: when a step() resolves multiple events (e.g.
      // perfect at beat 10.0 + auto-miss expiring at beat 10.5), the
      // state's combo reflects only the LAST mutation. Using
      // `previousCombo + 1` gives each event its true post-resolution
      // combo for FX / score / SFX. Same pattern Circles uses.
      const pianoVerdict: 'perfect' | 'good' | 'miss' =
        res.verdict === 'perfect' ? 'perfect' : res.verdict === 'miss' ? 'miss' : 'good';
      const eventCombo = pianoVerdict === 'miss' ? 0 : res.previousCombo + 1;
      this.spawnHitFx(res.lane, pianoVerdict, eventCombo, res.kind);
      const pan = (res.lane / (MANIA_LANE_COUNT - 1)) * 1.4 - 0.7;
      if (pianoVerdict === 'perfect' || pianoVerdict === 'good') {
        // `res.scoreDelta` is the engine's authoritative add — using it
        // (instead of re-deriving via baseScore × comboMultiplier) keeps
        // the score-pop label in lockstep with the actual HUD score
        // increment even if scoring constants change.
        this.spawnScorePop(res.lane, pianoVerdict, res.scoreDelta, eventCombo);
        this.audio.scheduleHitFeedback(pianoVerdict, undefined, {
          pan,
          comboNote: eventCombo,
        });
        if (pianoVerdict === 'perfect') {
          // Hit-stop micro-freeze: longer on big-combo perfects to
          // make peak performance moments feel crunchier.
          const stopMs =
            eventCombo >= BIG_COMBO_BREAK_THRESHOLD
              ? HIT_STOP_MS_PERFECT_BIG_COMBO
              : HIT_STOP_MS_PERFECT;
          this.renderer.requestHitStop(stopMs);
          this.maybeAddPerfectShake();
        } else {
          this.renderer.requestHitStop(HIT_STOP_MS_GOOD);
        }
        this.maybeCelebrateComboMilestone(eventCombo);
      } else {
        // Miss: pulse-duck the music, scale the duck + shake by the
        // size of the combo broken. Big-combo breaks also fire the
        // dedicated low-frequency "boom" stinger so the player
        // viscerally feels the lost streak.
        const isBigComboBreak = res.previousCombo >= BIG_COMBO_BREAK_THRESHOLD;
        this.addShake(isBigComboBreak ? 12 : 4);
        if (isBigComboBreak) {
          this.audio.scheduleComboBreak();
        }
        this.audio.scheduleHitFeedback('miss', undefined, { pan });
        const duck = isBigComboBreak ? MUSIC_DUCK_COMBO_BREAK : MUSIC_DUCK_MISS;
        this.audio.duckMusicPulse(duck.dip, duck.attackMs, duck.releaseMs);
        // Reset celebrated milestones so the next chain restarts the
        // banner sequence.
        this.comboMilestonesHit.clear();
      }
    } else if (ev.type === 'hp_changed') {
      const payload = ev.payload as ManiaHpChangedPayload;
      if (payload.reason === 'drained') {
        this.audio.playDeath();
        this.particleEmissionsThisFrame.push({
          type: 'death',
          x: this.cachedRect.width / 2,
          y: this.cachedRect.height * 0.5,
          colour: DEATH_COLOUR,
        });
        this.spawnBigBanner('GAME OVER', DEATH_COLOUR);
        this.addShake(14);
      } else if (payload.reason === 'lifeline') {
        this.spawnBigBanner('LIFELINE!', LIFELINE_COLOUR);
        this.addShake(10);
      } else if (payload.reason === 'empty_tap') {
        // Empty taps zero the engine combo without producing a
        // `note_judged` event, so the milestone-reset clear in the
        // miss branch above doesn't see them. Cover that path here.
        this.comboMilestonesHit.clear();
      }
    }
  }

  private finishSession(status: PianoSessionStatus): void {
    this.setStatus(status);
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    const loopsCleared = this.getLoopsCleared();
    this.outcome = {
      completed: this.state.completed || loopsCleared >= 1,
      score: this.state.score,
      combo: this.state.combo,
      maxCombo: this.state.maxCombo,
      perfects: this.state.perfects,
      goods: this.state.goods,
      misses: this.state.misses,
      accuracy: summariseManiaAccuracy(this.state),
      deathBeat: this.state.deathBeat,
      deathReason: this.state.deathReason ?? undefined,
    };
    this.audio.stopMusic();
    if (this.state.completed && this.state.score > 0) {
      this.audio.playVictorySting();
    } else if (!this.state.completed && loopsCleared === 0) {
      this.audio.playFailureSting();
    }
    this.opts.onComplete?.(this.outcome);
  }

  private buildTestHook(): PianoGameSessionTestHook {
    return {
      getOutcome: () => this.outcome,
      getStatus: () => this.status,
      getState: () => this.state,
      getBeat: () => this.audio.getCurrentBeat(),
      getEvents: () => this.allEvents.slice(),
      isAudioStarted: () => this.audio.isStarted,
    };
  }
}

export interface PianoGameSessionTestHook {
  getOutcome: () => ReplayOutcome | null;
  getStatus: () => PianoSessionStatus;
  getState: () => ManiaEngineState;
  getBeat: () => number;
  getEvents: () => ManiaEngineEvent[];
  isAudioStarted: () => boolean;
}

export { buildPerfectManiaReplayInputs } from '@shared/engine/mania/headlessRunner';
